import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import { HouseholdService } from 'app/cd/services/household-service';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';


@autoinject()
export class HouseholdCaseInformation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    protected householdCase: CM.HouseholdCase = {};

    //#region Household Members to Display on table
    protected householdMembers: CD.Member[] = []
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 5;
    //#endregion Household Members to Display on table

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly service: CaseService,
        private householdService: HouseholdService,
        ...rest: any[]) {
        super(...rest);

        this.checkPermissions();
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadHouseholdMembers();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadHouseholdCaseDetails();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHouseholdCaseDetails(): Promise<void> {
        if(!this.householdCaseId) return;

        const includes: string[] = [
            'metadata.created.byUser',
            'assignedTo',
            'history.assignments',
            'status',
            'program',
            'program.group'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadHouseholdCase(this.householdCaseId, includes);
            this.householdCase = response.results[0];
            await this.loadHouseholdMembers();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    // Load Household Members
    @apiCall
    private async loadHouseholdMembers(): Promise<void> {
        if(!this.householdCase.household?.id) return;

        try {
            this.state.beginLoad();
            const includes: string[] = [
                'primaryPlan.lineOfBusiness',
                'primaryPlan.healthPlan'
            ];

            const options: Partial<IQueryLoadOptions> = {
                top: this.pageSize,
                sorting: this.sorting,
                page: this.$pager?.page || 0,
                includeInactive: false
            };

            const response = await this.householdService.loadHouseholdMembers(this.householdCase.household.id, options, includes);
            this.householdMembers = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
